import React, { useEffect, useRef, useState } from 'react'
import Modal from './Modal'
import Button from '../ui/buttons/Button'
import Input from '../ui/inputs/Input'
import PhoneInput from '../ui/inputs/PhoneInput'
import Select from '../ui/selects/Select'
import { X, User2, Camera } from 'lucide-react'
import { fetchWithCsrf } from '../../utils/fetchWithCsrf'
import { snakecaseKeys } from '../../utils/snakecaseKeys'
import { camelizeKeys } from '../../utils/camelizeKeys'
import { useToast } from '../ui/toast/ToastContext'

const EditAccountModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phone: '',
    accountType: '',
    avatarUrl: ''
  })

  const [accountTypeOptions, setAccountTypeOptions] = useState([])
  const { addToast } = useToast()
  const avatarInputRef = useRef(null)

  useEffect(() => {
    fetch('/app/planner/account')
      .then(res => res.json())
      .then(data => {
        const camelData = camelizeKeys(data)
        setForm({
          firstName: camelData.firstName || '',
          lastName: camelData.lastName || '',
          username: camelData.slug || '',
          email: camelData.email || '',
          phone: camelData.phone || '',
          accountType: camelData.accountType || '',
          avatarUrl: camelData.avatarUrl || ''
        })
      })
      .catch(err => {
        console.error('Erro ao carregar perfil do admin', err)
        addToast({ type: 'error', message: 'Erro ao carregar dados da conta.' })
      })
  }, [])

  useEffect(() => {
    fetch('/app/shared/form_options')
      .then(res => res.json())
      .then(data => {
        const camelData = camelizeKeys(data)
        const options = camelData.accountTypes.map(o => ({ label: o.label, value: o.value }))
        setAccountTypeOptions(options)
      })
      .catch(err => {
        console.error('Erro ao carregar opções do formulário', err)
        addToast({ type: 'error', message: 'Erro ao carregar tipos de conta.' })
      })
  }, [])

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const payload = snakecaseKeys({ admin: form })

    fetchWithCsrf('/app/planner/account', {
      method: 'PUT',
      body: JSON.stringify(payload)
    })
      .then(async (res) => {
        if (!res.ok) {
          const json = await res.json()
          throw new Error(json.errors?.join(', ') || 'Erro ao atualizar perfil.')
        }

        addToast({ type: 'success', message: 'Informações atualizadas com sucesso!' })
        onSuccess()
      })
      .catch(err => {
        console.error('Erro ao atualizar perfil:', err)
        addToast({ type: 'error', message: err.message })
      })
  }

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
  
    const formData = new FormData()
    formData.append('avatar', file)
  
    fetchWithCsrf('/app/planner/account/avatar', {
      method: 'PUT',
      body: formData,
    })
      .then(async (res) => {
        if (!res.ok) {
          const json = await res.json()
          throw new Error(json.errors?.join(', ') || 'Erro ao atualizar avatar.')
        }
  
        const newUrl = URL.createObjectURL(file)
        setForm(prev => ({ ...prev, avatarUrl: newUrl }))
  
        addToast({ type: 'success', message: 'Avatar atualizado com sucesso!' })
      })
      .catch(err => {
        console.error('Erro ao atualizar avatar:', err)
        addToast({ type: 'error', message: err.message })
      })
  }  

  return (
    <Modal onClose={onClose}>
      <div className="p-10">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            <User2 size={24} className="text-tealMedium" strokeWidth={1.25} />
            Informações da conta
          </div>
          <button onClick={onClose}>
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        <div className="flex justify-center mb-8 relative w-44 h-44 mx-auto">
          {form.avatarUrl ? (
            <img
              src={form.avatarUrl}
              alt="Avatar"
              className="w-44 h-44 rounded-full object-cover"
            />
          ) : (
            <div className="w-44 h-44 rounded-full bg-gray-100 flex items-center justify-center">
              <User2 size={44} className="text-grayMedium" />
            </div>
          )}

          <button
            type="button"
            onClick={() => avatarInputRef.current.click()}
            className="flex items-center justify-center w-custom50 h-custom50 absolute -bottom-0 right-2 bg-white p-2 rounded-full hover:opacity-80"
          >
            <Camera size={28} strokeWidth={1.5} className="text-tealBase" />
          </button>

          <input
            type="file"
            accept="image/*"
            ref={avatarInputRef}
            className="hidden"
            onChange={handleAvatarUpload}
          />
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 font-jost text-sm">
          <Input name="firstName" label="Nome" required value={form.firstName} onChange={handleChange} />
          <Input name="lastName" label="Sobrenome" required value={form.lastName} onChange={handleChange} />
          <Input disabled name="username" label="Usuário" required value={form.username} onChange={handleChange} />
          <Select disabled name="accountType" label="Tipo da conta" value={form.accountType} onChange={handleChange} options={accountTypeOptions} />
          <Input name="email" label="E-mail" required value={form.email} onChange={handleChange} />
          <PhoneInput name="phone" label="Telefone" required value={form.phone} onChange={handleChange} />

          <div className="md:col-span-2 flex justify-end mt-1">
            <Button type="submit">SALVAR ALTERAÇÕES</Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default EditAccountModal
